(() => {
  if (customElements.get("pe-feedback-carousel")) return;
  customElements.define("pe-feedback-carousel", class extends HTMLElement {
    connectedCallback() {
      if (this.stop) return;
      const section = this.closest(".feedback-section");
      const track = this.querySelector("[data-feedback-track]");
      if (!track) return;
      const abort = new AbortController(), options = { signal: abort.signal };
      const cards = [...track.querySelectorAll(".feedback-card")];
      const prev = section.querySelector("[data-feedback-prev]");
      const next = section.querySelector("[data-feedback-next]");
      const toggle = section.querySelector("[data-feedback-toggle]");
      const reduced = matchMedia("(prefers-reduced-motion: reduce)");
      let step = 0, cycle = 0, position = 0, frame, last = 0;
      let visible = false, hover = false, dragging = false, focused = false, paused = false, idleUntil = 0, running = false;
      let touching = false;
      const normalize = value => {
        if (!cycle) return value;
        while (value < cycle) value += cycle;
        while (value >= cycle * 2) value -= cycle;
        return value;
      };
      const prepare = () => {
        const progress = step ? (normalize(track.scrollLeft) - cycle) / step : 0;
        track.querySelectorAll(".feedback-loop-clone").forEach(c => c.remove());
        track.classList.toggle("is-looping", cards.length > 1);
        if (cards.length > 1) {
          const copy = card => {
            const clone = card.cloneNode(true);
            clone.classList.add("feedback-loop-clone");
            clone.setAttribute("aria-hidden", "true");
            clone.setAttribute("inert", "");
            [clone, ...clone.querySelectorAll("*")].forEach(el => {
              el.removeAttribute("id");
              [...el.attributes].filter(a => a.name.startsWith("data-shopify")).forEach(a => el.removeAttribute(a.name));
            });
            return clone;
          };
          cards.slice().reverse().forEach(c => track.prepend(copy(c)));
          cards.forEach(c => track.append(copy(c)));
          step = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
          cycle = step * cards.length;
          position = normalize(cycle + progress * step);
          track.scrollLeft = position;
        } else cycle = 0;
        prev.disabled = next.disabled = cards.length < 2;
        toggle.hidden = cards.length < 2;
      };
      const paint = () => {
        const stopped = paused || reduced.matches;
        toggle.hidden = cards.length < 2 || reduced.matches;
        toggle.setAttribute("aria-pressed", String(stopped));
        toggle.setAttribute("aria-label", stopped ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel);
        toggle.querySelector("[data-feedback-pause]").hidden = stopped;
        toggle.querySelector("[data-feedback-play]").hidden = !stopped;
      };
      const move = direction => {
        idleUntil = performance.now() + 5000;
        position = normalize(track.scrollLeft + step * direction);
        track.scrollLeft = position;
      };
      prev.addEventListener("click", () => move(-1), options);
      next.addEventListener("click", () => move(1), options);
      toggle.addEventListener("click", () => { paused = !paused; paint(); }, options);
      this.addEventListener("pointerenter", e => { if (e.pointerType === "mouse") hover = true; }, options);
      this.addEventListener("pointerleave", () => { hover = false; }, options);
      this.addEventListener("pointerdown", () => { touching = dragging = true; idleUntil = performance.now() + 5000; }, options);
      const release = () => { if (!touching) return; touching = dragging = false; idleUntil = performance.now() + 5000; position = normalize(track.scrollLeft); };
      window.addEventListener("pointerup", release, options);
      window.addEventListener("pointercancel", release, options);
      section.addEventListener("focusin", () => { focused = true; }, options);
      section.addEventListener("focusout", e => { focused = section.contains(e.relatedTarget); }, options);
      track.addEventListener("scroll", () => {
        if (!running) {
          position = normalize(track.scrollLeft);
          if (Math.abs(position - track.scrollLeft) > 1 && !dragging) track.scrollLeft = position;
        }
      }, { ...options, passive: true });
      reduced.addEventListener("change", paint, options);
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }, { threshold: 0 });
      observer.observe(this);
      const resize = new ResizeObserver(prepare);
      resize.observe(this);
      const tick = now => {
        const elapsed = last ? Math.min(now - last, 50) : 0;
        last = now;
        const shouldRun = !!cycle && visible && !document.hidden && !reduced.matches && !paused && !hover && !focused && !dragging && now > idleUntil;
        if (shouldRun) {
          if (!running) position = normalize(track.scrollLeft);
          running = true;
          position = normalize(position + elapsed * .024);
          track.scrollLeft = position;
        } else running = false;
        frame = requestAnimationFrame(tick);
      };
      prepare(); paint(); frame = requestAnimationFrame(tick);
      this.stop = () => {
        abort.abort(); observer.disconnect(); resize.disconnect(); cancelAnimationFrame(frame);
        track.querySelectorAll(".feedback-loop-clone").forEach(c => c.remove());
        track.classList.remove("is-looping");
      };
    }
    disconnectedCallback() { this.stop?.(); this.stop = null; }
  });
})();
